import { useQuery } from '@tanstack/react-query';
import { fetchCurrentUser } from '../api/auth.js';

/**
 * Phase 43: lightweight current-user hook for role-based UI gating.
 *
 * Exposes { user, isAdmin, isTeamLead, isManager, isAccountant, isLoading } from /auth/me.
 * Cached via TanStack Query — NO new React Context needed.
 *
 * Usage:
 *   const { isManager } = useCurrentUser();
 *   if (isManager) return null; // hide button
 *
 * For mutation flows that depend on login/logout lifecycle, keep using the App-level
 * useAuth hook. This one is strictly for read-only role checks in leaf components.
 */
export function useCurrentUser () {
  const { data: user, isLoading } = useQuery({
    queryKey: ['currentUser'],
    queryFn: fetchCurrentUser,
    staleTime: 5 * 60 * 1000,
    retry: 1
  });

  const role = user?.role;
  return {
    user: user || null,
    isAdmin: role === 'ADMIN',
    isTeamLead: role === 'TEAM_LEAD',

    isManager: role === 'MANAGER' || role === 'ASSISTANT',
    isAccountant: role === 'ACCOUNTANT',
    isLoading
  };
}
